import React from 'react';
import { Box, Notification, Typography } from '@drone-js/core';
import { referrer } from 'core-framework/window/document';
import { useSelector } from 'react-redux';
import { selectCmsFromBootstrap, selectGeoFromBootstrap, selectIsLoggedIn } from 'store/slice/bootstrap';
import { StringFormatter } from '@agoda/react-handyman';
import { openWindowUrl } from 'core/window/windowService';
import ContentArea from 'component/common/ContentArea/ContentArea';
import { getDeSanitizeName } from 'core/utils/activityUrlGenerator';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesTypRedirectBannerCloseButtonClickEventActivitiesFieldsV1,
    ActivitiesTypRedirectBannerSeenEventActivitiesFieldsV1,
    ActivitiesTypRedirectBannerTextlinkClickEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesTypRedirectBannerCloseButtonClickEventActivitiesManifestV1,
    ActivitiesTypRedirectBannerSeenEventActivitiesManifestV1,
    ActivitiesTypRedirectBannerTextlinkClickEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { ActivityImage } from 'component/common/ActivityImage/ActivityImage';

const TYPRedirectBanner: React.FC = () => {
    const isLoggedIn = useSelector(selectIsLoggedIn);
    const cms = useSelector(selectCmsFromBootstrap);
    const geo = useSelector(selectGeoFromBootstrap);

    const rawReferrerUrl = referrer();
    const referrerUrl = rawReferrerUrl ? new URL(rawReferrerUrl) : '';
    const referrerPath = referrerUrl && referrerUrl.pathname;
    const bookingId = referrerUrl && (referrerUrl.searchParams.get('bookingId') || '');
    const isTypRedirected = !!bookingId && referrerPath.includes('thankyou');
    const [isShowBanner, setIsShowBanner] = React.useState(isTypRedirected);

    const CONFIRM_EMAIL_ETA = 10;
    const DISCOUNT_PERCENTAGE = 10;

    React.useEffect(() => {
        if (isShowBanner) {
            trackElementEvent<ActivitiesTypRedirectBannerSeenEventActivitiesFieldsV1>(ActivitiesTypRedirectBannerSeenEventActivitiesManifestV1.ids, {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShowBanner]);

    const onCloseClick = () => {
        setIsShowBanner(false);
        trackElementEvent<ActivitiesTypRedirectBannerCloseButtonClickEventActivitiesFieldsV1>(ActivitiesTypRedirectBannerCloseButtonClickEventActivitiesManifestV1.ids, {});
    };

    const onTextLinkClick = () => {
        trackElementEvent<ActivitiesTypRedirectBannerTextlinkClickEventActivitiesFieldsV1>(ActivitiesTypRedirectBannerTextlinkClickEventActivitiesManifestV1.ids, {});
        openWindowUrl('/account/bookings.html', '_blank');
    };

    return (
        <>
            {isShowBanner && (
                <Box
                    data-testid="activities-typ-redirect-banner"
                    data-element-name="activities-typ-redirect-banner"
                    borderStyle="solid"
                    borderColor="base.default"
                    borderWidth={['1', '0', '0', '0']}
                    backgroundColor="base.surface"
                    display="flex"
                    justifyContent="center"
                >
                    <ContentArea flex={1}>
                        <Box m="8">
                            <Notification
                                data-testid="activities-typ-redirect-banner-notification"
                                intent="positive"
                                title={cms[247433]}
                                description={StringFormatter.format(cms[247434], bookingId, CONFIRM_EMAIL_ETA)}
                                action={isLoggedIn ? cms[247435] : undefined}
                                closeButton={true}
                                onClose={onCloseClick}
                                onActionClick={onTextLinkClick}
                            />
                            <Box data-testid="activities-typ-redirect-banner-marketing" display="flex" alignItems="center" mt="8">
                                {/* eslint-disable-next-line @agoda/agoda-a11y/no-hardcoded-content -- @agoda/agoda-a11y/no-hardcoded-content[dc4f97aa-3212-4109-a96c-eb9695ed87be] */}
                                <ActivityImage
                                    maxWidth="126px"
                                    maxHeight="77px"
                                    alt="banner_image"
                                    me="8"
                                    src="//cdn6.agoda.net/cdn-activities/images/1/typ-redirect-marketing-banner.svg"
                                />
                                <Box display="flex" flexDirection="column">
                                    <Typography variant="body-s-strong" textColor="base.subtle" mb="8">
                                        {StringFormatter.format(cms[247436], DISCOUNT_PERCENTAGE)}
                                    </Typography>
                                    <Typography variant="body-s" textColor="base.subtle">
                                        {StringFormatter.format(cms[247437], getDeSanitizeName(geo?.cityNameTranslated) || '')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </ContentArea>
                </Box>
            )}
        </>
    );
};
TYPRedirectBanner.displayName = 'TYPRedirectBanner';

export default TYPRedirectBanner;
